/**
 * The built-in L-shaped (quarter-turn) stair (defs/stairL.ts) as a data spec.
 *
 * How it works — everything is driven by formulas of the live size, so every
 * group uses `fixed` resize (the formulas already give live coordinates):
 *  - The footprint is an L: the LOWER flight runs along the −n edge over the
 *    whole width, the UPPER flight runs up the +u end over the whole depth;
 *    they meet on a square corner landing of `flightWidth` × `flightWidth`.
 *    `fw` clamps the flight width so both flights keep at least 20 cm of run.
 *    `mirror` flips the turn side through `su` (+1 or −1 on every u).
 *  - Steps: `steps` = the combined run divided by the step, split over the
 *    flights by their share of the run (each flight keeps at least one).
 *    Each flight's treads are an `index` repeat: the group is drawn
 *    `stepsA` (resp. `stepsB`) times and the tread formulas read `i`.
 *  - Step ↔ number of steps: a step sync over the combined run. Editing the
 *    number of steps sets the step to run / count; any other change (size,
 *    flight width, step) recounts the steps. `exactStep` is off, like the
 *    built-in: the step is evened out to the run afterwards.
 *  - Walk line: along both flight centerlines, turning under the landing,
 *    from a cross tick (bottom) to a chevron head (top). The tick and head
 *    swap ends with `reverse direction` (two groups, one per direction).
 *  - Handles: the run-end handles sit on the lower flight's centerline and
 *    one depth handle on the upper flight's far end (it follows the mirror).
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec } from '../spec';

const W = 2.5;
const D = 2.0;
const FW = 0.9;
const HW = W / 2;
const HD = D / 2;
// The authored drawing = the formulas at the default size and params.
const RUN_A = W - FW;
const RUN_B = D - FW;
const STEPS = Math.round((RUN_A + RUN_B) / 0.25);
const STEPS_A = Math.round((STEPS * RUN_A) / (RUN_A + RUN_B));
const HEAD = Math.min(0.15, FW * 0.3);
const WALK_START_U = -HW + Math.min(0.2, 0.25 * RUN_A);
const WALK_N = -HD + FW / 2;
const WALK_TURN_U = HW - FW / 2;
const WALK_END_N = HD - Math.min(0.2, 0.25 * RUN_B);

export const stairLSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'FREE' },
  defaultSize: { width: W, depth: D, height: 3 },
  params: [
    { key: 'flightWidth', kind: 'float', label: 'flight width', default: FW, min: 0.5, max: 2.0, decimals: 2, distance: true, order: 11.3 },
    { key: 'treadDepth', kind: 'float', label: 'step', default: 0.25, min: 0.15, max: 0.4, decimals: 2, distance: true, order: 11.4 },
    { key: 'stepCount', kind: 'int', label: 'number of steps', default: 11, min: 2, order: 11.5 },
    { key: 'reversed', kind: 'boolean', label: 'reverse direction', default: false },
    { key: 'mirrored', kind: 'boolean', label: 'mirror', default: false },
  ],
  formulas: [
    { key: 'hw', label: 'Half width', expr: 'width / 2', note: 'Half the width (the lower flight runs from -hw to hw)' },
    { key: 'hd', label: 'Half depth', expr: 'depth / 2', note: 'Half the depth (the upper flight runs from -hd to hd)' },
    { key: 'su', label: 'Turn side', expr: '1 - 2 * mirrored', note: 'Turn side: +1 turns at the +u end, -1 (mirrored) at the -u end; scales every u' },
    { key: 'fw', label: 'Flight width', expr: 'max(0.1, min(flightWidth, width - 0.2, depth - 0.2))', note: 'Flight width used: clamped so both flights keep at least 20 cm of run' },
    { key: 'runA', label: 'Lower flight length', expr: 'max(0.05, width - fw)', note: 'Walkable run of the lower flight (up to the landing)' },
    { key: 'runB', label: 'Upper flight length', expr: 'max(0.05, depth - fw)', note: 'Walkable run of the upper flight (after the landing)' },
    { key: 'steps', label: 'Total steps', expr: 'max(2, round((runA + runB) / max(0.05, treadDepth)))', note: 'Total number of steps over both flights (the landing adds none)' },
    { key: 'stepsA', label: 'Steps on the lower flight', expr: 'min(steps - 1, max(1, round(steps * runA / (runA + runB))))', note: 'Steps of the lower flight: its share of the run, at least one per flight' },
    { key: 'stepsB', label: 'Steps on the upper flight', expr: 'steps - stepsA', note: 'Steps of the upper flight' },
    { key: 'headSize', label: 'Arrow size', expr: 'min(0.15, fw * 0.3)', note: 'Size of the walk line start tick and arrow head' },
    { key: 'walkStartU', label: 'Walk line start', expr: '(-hw + min(0.2, 0.25 * runA)) * su', note: 'Lower end of the walk line, inset from the start of the lower flight' },
    { key: 'walkN', label: 'Lower flight centerline', expr: '-hd + fw / 2', note: 'The lower flight centerline (n)' },
    { key: 'walkTurnU', label: 'Upper flight centerline', expr: '(hw - fw / 2) * su', note: 'The upper flight centerline (u), where the walk line turns' },
    { key: 'walkEndN', label: 'Walk line end', expr: 'hd - min(0.2, 0.25 * runB)', note: 'Upper end of the walk line, inset from the top of the upper flight' },
  ],
  shapes: [
    {
      id: 'outline',
      name: 'Outline',
      kind: 'polyline',
      points: [[-HW, -HD], [HW, -HD], [HW, HD], [HW - FW, HD], [HW - FW, -HD + FW], [-HW, -HD + FW], [-HW, -HD]],
      closed: false,
      expr: {
        'p0.u': '-hw * su',
        'p0.n': '-hd',
        'p1.u': 'hw * su',
        'p1.n': '-hd',
        'p2.u': 'hw * su',
        'p2.n': 'hd',
        'p3.u': '(hw - fw) * su',
        'p3.n': 'hd',
        'p4.u': '(hw - fw) * su',
        'p4.n': '-hd + fw',
        'p5.u': '-hw * su',
        'p5.n': '-hd + fw',
        'p6.u': '-hw * su',
        'p6.n': '-hd',
      },
    },
    {
      // Tread i + 1 of the lower flight; the last one is the landing's edge.
      id: 'lowerTread',
      name: 'Lower flight tread',
      kind: 'polyline',
      points: [[-HW + RUN_A / STEPS_A, -HD], [-HW + RUN_A / STEPS_A, -HD + FW]],
      closed: false,
      expr: { 'p0.u': '(-hw + (i + 1) * runA / stepsA) * su', 'p0.n': '-hd', 'p1.u': '(-hw + (i + 1) * runA / stepsA) * su', 'p1.n': '-hd + fw' },
    },
    {
      // Tread i of the upper flight; i = 0 is the landing's edge (the top edge is the outline).
      id: 'upperTread',
      name: 'Upper flight tread',
      kind: 'polyline',
      points: [[HW - FW, -HD + FW], [HW, -HD + FW]],
      closed: false,
      expr: { 'p0.u': '(hw - fw) * su', 'p0.n': '-hd + fw + i * runB / stepsB', 'p1.u': 'hw * su', 'p1.n': '-hd + fw + i * runB / stepsB' },
    },
    {
      id: 'walkLine',
      name: 'Walk line',
      kind: 'polyline',
      points: [[WALK_START_U, WALK_N], [WALK_TURN_U, WALK_N], [WALK_TURN_U, WALK_END_N]],
      closed: false,
      expr: { 'p0.u': 'walkStartU', 'p0.n': 'walkN', 'p1.u': 'walkTurnU', 'p1.n': 'walkN', 'p2.u': 'walkTurnU', 'p2.n': 'walkEndN' },
    },
    // Up the lower flight first: tick at the lower end, arrow head at the upper end.
    {
      id: 'startTick',
      name: 'Start tick',
      kind: 'polyline',
      points: [[WALK_START_U, WALK_N - HEAD], [WALK_START_U, WALK_N + HEAD]],
      closed: false,
      expr: { 'p0.u': 'walkStartU', 'p0.n': 'walkN - headSize', 'p1.u': 'walkStartU', 'p1.n': 'walkN + headSize' },
    },
    {
      id: 'arrowHead',
      name: 'Arrow head',
      kind: 'polyline',
      points: [[WALK_TURN_U - HEAD, WALK_END_N - HEAD], [WALK_TURN_U, WALK_END_N], [WALK_TURN_U + HEAD, WALK_END_N - HEAD]],
      closed: false,
      expr: {
        'p0.u': 'walkTurnU - headSize',
        'p0.n': 'walkEndN - headSize',
        'p1.u': 'walkTurnU',
        'p1.n': 'walkEndN',
        'p2.u': 'walkTurnU + headSize',
        'p2.n': 'walkEndN - headSize',
      },
    },
    // Reversed: tick at the upper end, arrow head at the lower end.
    {
      id: 'startTickReversed',
      name: 'Start tick (reversed)',
      kind: 'polyline',
      points: [[WALK_TURN_U - HEAD, WALK_END_N], [WALK_TURN_U + HEAD, WALK_END_N]],
      closed: false,
      expr: { 'p0.u': 'walkTurnU - headSize', 'p0.n': 'walkEndN', 'p1.u': 'walkTurnU + headSize', 'p1.n': 'walkEndN' },
    },
    {
      id: 'arrowHeadReversed',
      name: 'Arrow head (reversed)',
      kind: 'polyline',
      points: [[WALK_START_U + HEAD, WALK_N + HEAD], [WALK_START_U, WALK_N], [WALK_START_U + HEAD, WALK_N - HEAD]],
      closed: false,
      expr: {
        'p0.u': 'walkStartU + su * headSize',
        'p0.n': 'walkN + headSize',
        'p1.u': 'walkStartU',
        'p1.n': 'walkN',
        'p2.u': 'walkStartU + su * headSize',
        'p2.n': 'walkN - headSize',
      },
    },
  ],
  groups: [
    { id: 'outline', name: 'Outline', shapeIds: ['outline'], resize: { u: 'fixed', n: 'fixed' } },
    { id: 'lowerTreads', name: 'Lower flight treads', shapeIds: ['lowerTread'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { expr: 'stepsA' } } },
    { id: 'upperTreads', name: 'Upper flight treads', shapeIds: ['upperTread'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { expr: 'stepsB' } } },
    { id: 'walkLine', name: 'Walk line', shapeIds: ['walkLine'], resize: { u: 'fixed', n: 'fixed' } },
    { id: 'arrow', name: 'Arrow (up)', shapeIds: ['startTick', 'arrowHead'], resize: { u: 'fixed', n: 'fixed' }, visibleWhen: { param: 'reversed', equals: false } },
    {
      id: 'arrowReversed',
      name: 'Arrow (reversed)',
      shapeIds: ['startTickReversed', 'arrowHeadReversed'],
      resize: { u: 'fixed', n: 'fixed' },
      visibleWhen: { param: 'reversed', equals: true },
    },
  ],
  handles: {
    start: { u: '-hw', n: 'walkN' },
    end: { u: 'hw', n: 'walkN' },
    depth: [{ label: 'Upper flight end handle', side: 1, at: { u: 'walkTurnU', n: 'hd' } }],
  },
  stepSyncs: [{ label: 'Step size and step count', step: 'treadDepth', count: 'stepCount', run: 'runA + runB', minCount: 2, exactStep: false }],
  model3d: null,
};
